// Guest cart buffer — a PRE-AUTH STAGING BUFFER, not a cart.
//
// Project rule #7 forbids localStorage as a SUBSTITUTE for a backend API. This
// is not one: no anonymous-cart API exists to call, and the buffer is replayed
// into the authoritative API the moment one is reachable (cart-context replay).
// The property that keeps that true is that ONLY product ids and quantities are
// stored — never prices, totals, tax, promo state, email, or tokens. All money
// values stay server-computed, so this can never disagree with the real cart.
//
// Persistence is sessionStorage (not localStorage) so the buffer dies with the
// tab. sessionStorage access can throw — Safari Private Browsing historically
// threw on setItem, quota can be exceeded, and sandboxed iframe embed contexts
// can deny access entirely — so every access is wrapped and degrades to a
// no-op / empty buffer rather than crashing the storefront. The stored payload
// is also treated as untrusted input: it may be absent, non-JSON, valid JSON
// of the wrong shape, or an array containing malformed entries.

export interface GuestCartEntry {
  product_id: string;
  quantity: number;
}

export const GUEST_CART_KEY = "aldelo-mkt-guest-cart";
export const MAX_GUEST_ENTRIES = 50;
/**
 * Per-product quantity ceiling. Exported because cart-context clamps to the
 * same value when it collapses duplicate product ids and when a guest edits a
 * staged quantity — a second hardcoded `99` there would silently disagree with
 * this module the moment the ceiling moves.
 */
export const MAX_GUEST_QUANTITY = 99;

function isValidEntry(v: unknown): v is GuestCartEntry {
  if (typeof v !== "object" || v === null) return false;
  const e = v as Record<string, unknown>;
  return (
    typeof e.product_id === "string" &&
    e.product_id.length > 0 &&
    typeof e.quantity === "number" &&
    Number.isInteger(e.quantity) &&
    e.quantity > 0 &&
    e.quantity <= MAX_GUEST_QUANTITY
  );
}

/**
 * Keep the MOST RECENT entries when over cap, dropping the OLDEST from the
 * front. The buffer is append-most-recent — `addGuestEntry` pushes new entries
 * at the tail and accumulates in place — so the tail holds the freshest state.
 * A naive `slice(0, MAX)` keeps the head (oldest) and evicts the JUST-ADDED
 * entry when the buffer is full, which is the opposite of the intended
 * "newest wins" cap semantic (TD-310): a customer who fills the buffer and
 * adds one more loses the thing they just added, not some stale early entry.
 */
function capEntries(entries: GuestCartEntry[]): GuestCartEntry[] {
  if (entries.length <= MAX_GUEST_ENTRIES) return entries;
  return entries.slice(entries.length - MAX_GUEST_ENTRIES);
}

/** Read the buffer. Any corruption degrades to [] — never throws. */
export function readGuestCart(): GuestCartEntry[] {
  try {
    const raw = sessionStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return capEntries(
      parsed
        .filter(isValidEntry)
        .map((e) => ({ product_id: e.product_id, quantity: e.quantity })),
    );
  } catch {
    return [];
  }
}

/** Persist the buffer, normalised and capped. Storage failures are non-fatal. */
export function writeGuestCart(entries: GuestCartEntry[]): void {
  try {
    const safe = capEntries(
      entries
        .filter(isValidEntry)
        .map((e) => ({ product_id: e.product_id, quantity: e.quantity })),
    );
    sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(safe));
  } catch {
    // Quota or disabled storage — the embed still works, just without staging.
  }
}

/**
 * Add a product, accumulating quantity when already present. Returns the new
 * buffer. A bad `quantity` (NaN, non-integer, zero, or negative) or empty
 * `productId` rejects the call and returns the buffer unchanged — it must
 * NOT reach the accumulate branch below, because `Math.min(existing.quantity
 * + quantity, MAX_GUEST_QUANTITY)` combined with isValidEntry filtering in
 * writeGuestCart would otherwise silently DELETE the existing entry (NaN/
 * non-integer results fail isValidEntry's Number.isInteger check; negative
 * results can fail the `> 0` check) rather than leaving it untouched.
 */
export function addGuestEntry(productId: string, quantity: number): GuestCartEntry[] {
  if (
    typeof productId !== "string" ||
    productId.length === 0 ||
    !Number.isInteger(quantity) ||
    quantity <= 0
  ) {
    return readGuestCart();
  }
  const current = readGuestCart();
  const existing = current.find((e) => e.product_id === productId);
  if (existing) {
    existing.quantity = Math.min(existing.quantity + quantity, MAX_GUEST_QUANTITY);
  } else {
    current.push({ product_id: productId, quantity });
  }
  writeGuestCart(current);
  return readGuestCart();
}

/**
 * Clear the buffer (e.g. after a successful replay into the real cart, on
 * checkout completion, or on sign-out). Returns true when the buffer was
 * cleared (or already empty), false when a storage error prevented removal.
 *
 * The return value matters because a FAILED clear is the dangerous case
 * (TD-310): the local cart state is emptied regardless, but if the buffer
 * survives in sessionStorage the next sign-in replays items the customer
 * already purchased (UpsertLineInTx ADDS, so a double-replay doubles
 * quantities — see cart-context's replayGuestCart). Surfacing the failure
 * lets callers decide; the `console.warn` (§42) keeps it from vanishing
 * entirely, matching the structured-logging discipline used on other
 * storage-failure paths in this embed.
 */
export function clearGuestCart(): boolean {
  try {
    sessionStorage.removeItem(GUEST_CART_KEY);
    return true;
  } catch (e) {
    console.warn("[guest-cart] clearGuestCart failed; buffer may persist", e);
    return false;
  }
}
